import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type {
  EvLaststufe, EvMessstelle, EvModellanschluss, EvNshv, EvOrt, EvPunkt, EvVerteiler, EvVorgabe, EventstromStand,
} from './elektro/eventstrom';
import {
  ANNAHMEN_HINWEIS, BILANZ_HINWEIS, PDF_HINWEIS, eventStatusText, steckername,
} from './elektro/eventstrom';
import { zerlegeBefund } from './elektro/sicherheitstechnik';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Eventstromversorgung (W2-B1, 16.10.2026) — Abschnitt „Netz und Verteilung".
 *
 * Die Seite LIEST `GET …/elektro/eventstrom`: die Anschlusspunkte der FLB (Kap. 8.8) und ihr Plan je Ort,
 * Laststufen FLB gegen Modell, Event-Bilanz je Ort gegen Verteiler und Zuleitung, Selektivitäts-Vorprüfung,
 * Zusatzschutz, Eventverteiler aus Modell, Fachplaner-Bilanz und SIMARIS und die MID-Messstellenliste.
 * Gerechnet wird auf dem Server; die Seite schreibt nichts, auch nicht in die Leistungsbilanz.
 *
 * Ein PDF-Knopf (ELT-EVS) und die Excel-Mappe. Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung an, sonst die abgelegte (F11) —
 * das steht am Knopf und über der Seite.
 */
export function ElektroEventstrom() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <EventstromAnsicht api={api} projektId={projekt.id} />;
}

function zustandVon(status: string): Zustand {
  switch (status) {
    case 'fehler': return 'fehler';
    case 'warnung': case 'fehlt_im_modell': return 'warnung';
    case 'ok': case 'gleich': return 'gut';
    default: return 'info';
  }
}

function Statusmarke({ status }: { status: string }) {
  return <StatusBadge zustand={zustandVon(status)} text={eventStatusText(status)} klein />;
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

function zahl(wert: number | null, stellen = 1): string {
  return wert === null ? '—' : wert.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

export function EventstromAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<EventstromStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroEventstrom(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'nachweis' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'nachweis') await api.elektroEventstromNachweisLaden(projektId);
      else await api.elektroEventstromListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('nachweis'); }}>
        Nachweis als PDF
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Eventstromversorgung"
        beschreibung="Anschlusspunkte der FLB (Powerlock, CEE) je Ort gegen Modell, Netzberechnung, Fachplaner-Bilanz und SIMARIS: Laststufen, Event-Bilanz, Zuleitung, Selektivität, Zusatzschutz und MID-Messstellen. Gerechnet beim Abruf, nur lesend."
        aktionen={aktionen}
      />
      <p className="hinweis">{PDF_HINWEIS}</p>
      {fehler && <ErrorState titel="Die Eventstromversorgung konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && <Inhalt stand={stand} />}
    </div>
  );
}

function Inhalt({ stand }: { stand: EventstromStand }) {
  const k = stand.kennzahlen;
  return (
    <>
      <p className="hinweis">{BILANZ_HINWEIS}</p>
      {!k.flbEingelesen && (
        <p className="hinweis hinweis-warnung">
          Die Anschlusspunkte der FLB sind noch nicht eingelesen (Werkzeug „eventstrom-einlesen“). Gezeigt werden nur
          die Eventverteiler und die Anschlusspunkte des Modells.
        </p>
      )}
      {k.aufAnnahmen && <p className="hinweis hinweis-warnung">{ANNAHMEN_HINWEIS}</p>}
      {stand.flbQuelle && <p className="klein">FLB: {stand.flbQuelle}</p>}
      <KpiReihe>
        <KpiCard titel="Anschlusspunkte FLB" wert={k.punkteFlb} vergleich={`an ${k.orte} Orten · Modell ${k.punkteModell}`}
                 zustand={k.punkteModell < k.punkteFlb ? 'warnung' : 'gut'}
                 zustandText={k.punkteModell < k.punkteFlb ? 'fehlen im Modell' : 'vollständig'} />
        <KpiCard titel="Anschlussleistung FLB" wert={`${zahl(k.anschlussKvaFlb)} kVA`} vergleich={`P_bed ${zahl(k.bedarfKwFlb)} kW`} />
        <KpiCard titel="nicht abgebildet" wert={`${zahl(k.nichtAbgebildetKw)} kW`}
                 vergleich={`${k.verteilerNichtImModell} von ${k.eventverteiler} Eventverteilern nicht im Modell`} />
        <KpiCard titel="MID-Messstellen" wert={k.messstellen} vergleich={`im Modell ${k.messstellenImModell}`}
                 zustand={k.messstellenImModell < k.messstellen ? 'warnung' : 'gut'}
                 zustandText={k.messstellenImModell < k.messstellen ? 'fehlen' : 'vollständig'} />
        <KpiCard titel="Warnungen" wert={k.warnungen} zustand={k.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.warnungen > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Fehler" wert={k.fehler} zustand={k.fehler > 0 ? 'fehler' : 'gut'}
                 zustandText={k.fehler > 0 ? 'beheben' : 'keine'} />
      </KpiReihe>
      <Orte zeilen={stand.orte} punkte={stand.punkte} />
      <Laststufen zeilen={stand.laststufen} />
      <Punkte zeilen={stand.punkte} />
      <Verteiler zeilen={stand.verteiler} />
      <Nshv zeilen={stand.nshv} />
      <Modell zeilen={stand.modellanschluesse} />
      <Messstellen zeilen={stand.messstellen} />
      <Vorgaben zeilen={stand.vorgaben} />
      {stand.befunde.length > 0 && (
        <ContentSection titel="Allgemeine Befunde">
          <Befundliste befunde={stand.befunde} />
        </ContentSection>
      )}
    </>
  );
}

function Orte({ zeilen, punkte }: { zeilen: EvOrt[]; punkte: EvPunkt[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<EvOrt>[] = [
    { schluessel: 'ort', titel: 'Ort', wert: (z) => z.ort },
    { schluessel: 'punkte', titel: 'Punkte', art: 'zahl', wert: (z) => z.punkte },
    { schluessel: 'summe', titel: 'Σ I_n', einheit: 'A', art: 'zahl', nachkomma: 0, wert: (z) => z.summeNennstromA },
    { schluessel: 's', titel: 'S_n', einheit: 'kVA', art: 'zahl', nachkomma: 1, wert: (z) => z.anschlussKva },
    { schluessel: 'g', titel: 'g', art: 'zahl', nachkomma: 2, wert: (z) => z.gleichzeitigkeit },
    { schluessel: 'pbed', titel: 'P_bed', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.bedarfKw },
    { schluessel: 'ib', titel: 'I_B', einheit: 'A', art: 'zahl', nachkomma: 0, wert: (z) => z.bemessungsstromA },
    { schluessel: 'vt', titel: 'Verteiler', wert: (z) => (z.verteiler ? `${z.verteiler}${z.verteilerImModell ? '' : ' *'}` : null) },
    { schluessel: 'pvt', titel: 'P Verteiler', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.verteilerKw },
    { schluessel: 'zul', titel: 'Zuleitung', einheit: 'A', art: 'zahl', nachkomma: 0, wert: (z) => z.zuleitungA },
    { schluessel: 'sel', titel: 'I_n,Z / max', art: 'zahl', nachkomma: 2, wert: (z) => z.selektivitaetsverhaeltnis },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} /> },
  ];
  return (
    <ContentSection titel="Event-Bilanz je Ort"
                    beschreibung="S_n = √3 · U · Σ I_n, P_bed = g · S_n · cos φ, I_B = g · Σ I_n. Verteiler: von Hand zugeordnet oder über den Namen; * = nicht im Modell. Zuleitung: Leistungsschalter laut SIMARIS, sonst Schutzorgan im Modell. Selektivität: Vorprüfung I_n,Zuleitung ≥ k · größter Punkt.">
      <DataTable name={`Orte (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.ort} suche={false} dicht />
      {zeilen.map((z) => (
        <div key={z.ort} className="karte">
          <h3>{z.ort}</h3>
          <p className="klein">
            {punkte.filter((p) => p.ort === z.ort)
              .map((p) => `${p.anzahl} × ${p.nennstromA} A ${steckername(p.stecker)} ${p.zweck}`).join(' · ')}
          </p>
          <Befundliste befunde={z.befunde} />
        </div>
      ))}
    </ContentSection>
  );
}

function Laststufen({ zeilen }: { zeilen: EvLaststufe[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<EvLaststufe>[] = [
    { schluessel: 'stecker', titel: 'Steckverbinder', wert: (z) => steckername(z.stecker) },
    { schluessel: 'in', titel: 'I_n', einheit: 'A', art: 'zahl', wert: (z) => z.nennstromA },
    { schluessel: 's', titel: 'S je Punkt', einheit: 'kVA', art: 'zahl', nachkomma: 1, wert: (z) => z.anschlussKvaJePunkt },
    { schluessel: 'flb', titel: 'FLB', art: 'zahl', wert: (z) => z.flb },
    { schluessel: 'modell', titel: 'Modell', art: 'zahl', wert: (z) => z.modell },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} /> },
  ];
  return (
    <ContentSection titel="Laststufen: FLB gegen Modell"
                    beschreibung="Je Steckverbinder und Nennstrom: wie viele Punkte die FLB fordert und wie viele im Modell stehen.">
      <DataTable name="Laststufen" spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.stecker}|${z.nennstromA}`} suche={false} dicht />
    </ContentSection>
  );
}

function Punkte({ zeilen }: { zeilen: EvPunkt[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<EvPunkt>[] = [
    { schluessel: 'ort', titel: 'Ort', wert: (z) => z.ort },
    { schluessel: 'n', titel: 'n', art: 'zahl', wert: (z) => z.anzahl },
    { schluessel: 'in', titel: 'I_n', einheit: 'A', art: 'zahl', wert: (z) => z.nennstromA },
    { schluessel: 'stecker', titel: 'Steckverbinder', wert: (z) => steckername(z.stecker) },
    { schluessel: 'zweck', titel: 'Zweck', wert: (z) => z.zweck },
    { schluessel: 'schutz', titel: 'Zusatzschutz', wert: (z) => z.zusatzschutz },
    { schluessel: 'messung', titel: 'Messung', wert: (z) => z.messung },
  ];
  return (
    <ContentSection titel="Anschlusspunkte: Schutz und Messung"
                    beschreibung="Aus der FLB, Kap. 8.8.2 (eingelesen mit eventstrom-einlesen). Alle Punkte sollen einzeln messbar sein.">
      <DataTable name={`Anschlusspunkte (${zeilen.reduce((s, z) => s + z.anzahl, 0)})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.ort}|${z.nennstromA}|${z.stecker}|${z.zweck}`} dicht />
    </ContentSection>
  );
}

function Verteiler({ zeilen }: { zeilen: EvVerteiler[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<EvVerteiler>[] = [
    { schluessel: 'kz', titel: 'Verteiler', wert: (z) => `${z.kennzeichen}${z.imModell ? '' : ' *'}` },
    { schluessel: 'bez', titel: 'Bezeichnung', wert: (z) => z.bezeichnung },
    { schluessel: 'ober', titel: 'Ober', wert: (z) => z.ober },
    { schluessel: 'pm', titel: 'P Modell', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.modellKw },
    { schluessel: 'pq', titel: 'P Fachplaner-Bilanz', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.quelleKw },
    { schluessel: 'sim', titel: 'SIMARIS', wert: (z) => (z.simarisInA === null ? null
      : `${z.simarisStart ?? '?'}: ${z.simarisInA} A ${z.simarisAusloeser ?? ''}, g ${z.simarisGleichzeitigkeit ?? '—'}`) },
    { schluessel: 'na', titel: 'nicht abgebildet', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.nichtAbgebildetKw },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} /> },
  ];
  return (
    <ContentSection titel="Event- und Bühnenverteiler"
                    beschreibung="Erkannt am Namen (Showpower, Bühne, Event) in Modell, Leistungsbilanz der Fachplanung oder SIMARIS. * = nicht im Modell. „Nicht abgebildet“: Fachplaner-Bilanz minus Modell, ganz, wenn der Verteiler im Modell fehlt oder keine Ober-NSHV hat.">
      <DataTable name={`Eventverteiler (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.kennzeichen} suche={false} dicht />
      {zeilen.filter((z) => z.befunde.length > 0).map((z) => (
        <div key={z.kennzeichen} className="karte">
          <h3>{z.kennzeichen}</h3>
          <Befundliste befunde={z.befunde} />
        </div>
      ))}
    </ContentSection>
  );
}

function Nshv({ zeilen }: { zeilen: EvNshv[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<EvNshv>[] = [
    { schluessel: 'nshv', titel: 'NSHV', wert: (z) => z.nshv },
    { schluessel: 'vt', titel: 'Eventverteiler', art: 'zahl', wert: (z) => z.verteiler },
    { schluessel: 'pm', titel: 'P Modell', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.modellKw },
    { schluessel: 'pq', titel: 'P Fachplaner-Bilanz', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.quelleKw },
    { schluessel: 'na', titel: 'nicht abgebildet', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.nichtAbgebildetKw },
  ];
  return (
    <ContentSection titel="Eventlast je NSHV"
                    beschreibung="Vorschau — die Leistungsbilanz des Modells bleibt unverändert.">
      <DataTable name={`NSHV (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.nshv} suche={false} dicht />
    </ContentSection>
  );
}

function Modell({ zeilen }: { zeilen: EvModellanschluss[] }) {
  const spalten: Spalte<EvModellanschluss>[] = [
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.typ },
    { schluessel: 'in', titel: 'I_n', einheit: 'A', art: 'zahl', wert: (z) => z.nennstromA },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer },
    { schluessel: 'raumname', titel: 'Raumname', wert: (z) => z.raumname },
    { schluessel: 'vt', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'ev', titel: 'Eventverteiler', art: 'status', wert: (z) => z.verteilerIstEvent,
      inhalt: (z) => <StatusBadge zustand={z.verteilerIstEvent ? 'gut' : 'warnung'} text={z.verteilerIstEvent ? 'ja' : 'nein'} klein /> },
    { schluessel: 'kreis', titel: 'Kreis', wert: (z) => z.kreis },
  ];
  return (
    <ContentSection titel="Event-Anschlusspunkte im Modell"
                    beschreibung="Kraftsteckdosen in Show-, Bühnen- und Eventräumen oder an Eventverteilern.">
      {zeilen.length === 0
        ? <p className="klein">Im Modell steht kein Event-Anschlusspunkt.</p>
        : <DataTable name={`Modellanschlüsse (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                     schluessel={(z) => `${z.raumnummer ?? ''}|${z.kreis ?? ''}|${z.typ ?? ''}`} suche={false} dicht />}
    </ContentSection>
  );
}

function Messstellen({ zeilen }: { zeilen: EvMessstelle[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<EvMessstelle>[] = [
    { schluessel: 'nr', titel: 'Nr', art: 'zahl', wert: (z) => z.nr },
    { schluessel: 'ort', titel: 'Ort', wert: (z) => z.ort },
    { schluessel: 'punkt', titel: 'Anschlusspunkt', wert: (z) => z.punkt },
    { schluessel: 'anf', titel: 'Anforderung', wert: (z) => z.anforderung },
    { schluessel: 'modell', titel: 'im Modell', art: 'status', wert: (z) => z.imModell,
      inhalt: (z) => <StatusBadge zustand={z.imModell ? 'gut' : 'warnung'} text={z.imModell ? 'ja' : 'nein'} klein /> },
  ];
  return (
    <ContentSection titel="MID-Messstellenliste"
                    beschreibung="Je Anschlusspunkt eine Messstelle (FLB 8.8.2: separat messbar; 8.8.4: MID-konform, Lastgang).">
      <DataTable name={`Messstellen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => String(z.nr)} dicht />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: EvVorgabe[] }) {
  const spalten: Spalte<EvVorgabe>[] = [
    { schluessel: 'feld', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts', wert: (z) => z.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => (z.rueckfall ? `${z.herkunft} (Rückfall)` : z.herkunft) },
    { schluessel: 'bestaetigt', titel: 'bestätigt', art: 'status', wert: (z) => z.bestaetigt,
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein /> },
    { schluessel: 'norm', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];
  return (
    <ContentSection titel="Vorgaben"
                    beschreibung="Gepflegt unter Elektro-Vorgaben (elektro_vorgaben). „Rückfall“ heißt: keine Zeile im Projekt, gerechnet mit dem Wert der Migration 0950. Ein Ort lässt sich über ev_ort_verteiler (Verwendung = Ort) von Hand einem Verteiler zuordnen.">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.feld}|${z.verwendung ?? ''}`} dicht />
    </ContentSection>
  );
}
